import { useLayoutEffect, type RefObject } from "react";

const SCROLL_LEFT_VAR = "--gantt-scroll-left";
const SCROLL_TOP_VAR = "--gantt-scroll-top";

/**
 * Keeps the dependency arrows out from under the sticky chrome.
 *
 * The arrows are one SVG drawn over the rows, so they paint above everything
 * in them — including the sticky title column, whose cells live inside the
 * rows' own stacking context and so can never be raised over a sibling layer.
 * Scrolled sideways, an arrow ran straight across the issue titles; scrolled
 * down, across the axis header. So the arrow layer is clipped instead, to the
 * part of the chart that is actually showing timeline.
 *
 * The scroll offsets are published as CSS variables on the scroller rather than
 * as state: a scroll must not re-render a chart of several hundred rows.
 */
export function useStickyChromeClip(scrollRef: RefObject<HTMLElement>) {
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const publish = () => {
      el.style.setProperty(SCROLL_LEFT_VAR, `${el.scrollLeft}px`);
      el.style.setProperty(SCROLL_TOP_VAR, `${el.scrollTop}px`);
    };
    publish();
    el.addEventListener("scroll", publish, { passive: true });
    return () => el.removeEventListener("scroll", publish);
  }, [scrollRef]);
}

/**
 * The `clip-path` for a layer placed at the chart body's origin, i.e. just
 * right of the title column and `offsetTop` px below the axis header.
 *
 * The title column covers the scroller's first `labelWidth` px of view, which
 * in the layer's own coordinates is everything left of `scrollLeft`; the header
 * covers everything above `scrollTop - offsetTop`.
 */
export function stickyChromeClipPath(offsetTop: number): string {
  return `inset(max(0px, calc(var(${SCROLL_TOP_VAR}, 0px) - ${offsetTop}px)) 0px 0px var(${SCROLL_LEFT_VAR}, 0px))`;
}
